<script>
   /*************************************************************
   * ModelLoader                                                *
   * -------------------------                                  *
   * loads SIMCA model from file                                *
   **************************************************************/

   import FileSelector from '../../../shared/controls/FileSelector.svelte';
   import Container from '../../../shared/controls/Container.svelte';
   import Button from '../../../shared/controls/Button.svelte';
   import ButtonCancel from '../../../shared/controls/ButtonCancel.svelte';

   // calibration set
   export let model;
   export let simca;

   // JSON file with model
   let modelFile = undefined;

   // app status for handling and showing errors
   let status = 'neutral';
   let errMsg = '';


   // gets model content as one large string, parse it into matrix
   function setModel(strData) {
      try{
         const m = JSON.parse(strData);

         // check if the object version is from previous app version (no hash field)
         if (!Object.keys(m.pca).includes('hash')) {
            throw Error('you try to load model incompatible with this app version, create a new model or use <a href="/ddsimca-old/">old version</a> of the app.');
         }
         m.pca.v = new Float64Array(m.pca.v);

         m.pca.lim.classic.Nh = new Float64Array(m.pca.lim.classic.Nh);
         m.pca.lim.classic.Nq = new Float64Array(m.pca.lim.classic.Nq);
         m.pca.lim.classic.h0 = new Float64Array(m.pca.lim.classic.h0);
         m.pca.lim.classic.q0 = new Float64Array(m.pca.lim.classic.q0);

         m.pca.lim.classic.Ng = new Float64Array(m.pca.lim.classic.Ng);
         m.pca.lim.classic.Nr = new Float64Array(m.pca.lim.classic.Nr);
         m.pca.lim.classic.g0 = new Float64Array(m.pca.lim.classic.g0);
         m.pca.lim.classic.r0 = new Float64Array(m.pca.lim.classic.r0);

         m.pca.lim.robust.Nh = new Float64Array(m.pca.lim.robust.Nh);
         m.pca.lim.robust.Nq = new Float64Array(m.pca.lim.robust.Nq);
         m.pca.lim.robust.h0 = new Float64Array(m.pca.lim.robust.h0);
         m.pca.lim.robust.q0 = new Float64Array(m.pca.lim.robust.q0);

         m.pca.lim.robust.Ng = new Float64Array(m.pca.lim.robust.Ng);
         m.pca.lim.robust.Nr = new Float64Array(m.pca.lim.robust.Nr);
         m.pca.lim.robust.g0 = new Float64Array(m.pca.lim.robust.g0);
         m.pca.lim.robust.r0 = new Float64Array(m.pca.lim.robust.r0);

         m.simca.h0 = new Float64Array(m.simca.h0);
         m.simca.Nh = new Float64Array(m.simca.Nh);
         m.simca.q0 = new Float64Array(m.simca.q0);
         m.simca.Nq = new Float64Array(m.simca.Nq);
         m.simca.Nf = new Float64Array(m.simca.Nf);

         m.simca.g0 = new Float64Array(m.simca.g0);
         m.simca.Ng = new Float64Array(m.simca.Ng);
         m.simca.r0 = new Float64Array(m.simca.r0);
         m.simca.Nr = new Float64Array(m.simca.Nr);
         m.simca.Ni = new Float64Array(m.simca.Ni);

         m.simca.eCrit = new Float64Array(m.simca.eCrit);
         m.simca.oCrit = new Float64Array(m.simca.oCrit);
         m.simca.veCrit = new Float64Array(m.simca.veCrit);
         m.simca.voCrit = new Float64Array(m.simca.voCrit);

         model = m.pca;
         simca = m.simca;
         status = 'success';
         errMsg = ''
      } catch (e) {
         model = null;
         simca = null;
         status = 'error';
         errMsg = e;
      }
   }

   // promise for reading the CSV file
   function readFile (e) {
      const reader = new FileReader();
      reader.onload = () => { setModel(reader.result) };
      reader.readAsText(modelFile);
   }

   function reset() {
      modelFile = null
   }

   // reactive expression for tracking changes of 'dataFile'
   $: if (modelFile === null) {
      // if model file is empty reset status and error messages
      model = null;
      simca = null;
   } else {
      // if not just remove error messages if any
      status = 'neutral';
      errMsg = '';
   }

   // file name with model without extension
   $: modelFileName = modelFile ? modelFile.name.replace(/\.[^/.]+$/, "") : '';
</script>

{#if !model }

   <!-- show if data is not loaded -->
   <!-- reset component if dataFile is changed inside -->
   {#key modelFile === undefined}
   <Container>
      <FileSelector id="modelFile" bind:file={modelFile} acceptType=".json" message="Select file with model"/>
   </Container>
   {/key}

   {#if modelFile}
   <Container label="&nbsp;">
      <Button on:click={readFile}>Load</Button>
   </Container>
   {/if}

{:else}

   <!-- dataset is loaded - show info and cancel button -->
   <Container>
      <p class="model-info">
         <span>Model file:</span> <span title="{modelFileName}" class="file-name">{modelFileName}</span><br>
         <span>Target class:</span> <span title="{simca.className}" class="class-name">{simca.className}</span>
         {#if model.exclvars && model.exclvars.length > 0}<br><span>{model.exclvars.length} variables excluded.</span>{/if}
      </p>
      <ButtonCancel on:click={reset} />
   </Container>

{/if}

<!-- error message -->
{#if status === 'error' || errMsg}
<Container status="error">
   <p>{@html errMsg}</p>
</Container>
{/if}


<style>

   .model-info {
      line-height: 1.5em;
   }
   .model-info > :global(span) {
      vertical-align:top;
      color: #a0a0a0;
   }

   .model-info > .file-name,
   .model-info > .class-name {
      display: inline-block;
      padding: 0;
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 25ch;
      color: #222;
   }

</style>